import {
    AfterContentInit,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef
} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {DynamicFormConfig} from '../dynamic-form.config';
import {FieldTypeToControlBuilder} from '../../dynamic-field/field-type-to-control-builder.type';
import {FieldTypeToComponent} from '../../fields/field-type-to-component';
import {fieldTypeToControlBuilder} from '../../fields/field-type-to-control-builder';
import {DynamicFormService} from '../dynamic-form.service';
import {FieldTypeToControlUpdater} from '../../dynamic-field/field-type-to-control-updater.type';
import {fieldTypeToControlUpdater} from '../../fields/field-type-to-control-updater';
import {DynamicFormEventsService} from '../../events/dynamic-form-events.service';
import {DynamicFormValidationService} from '../../validation/dynamic-form-validation.service';
import {EventDispatcherService} from '../../events/event-dispatcher.service';
import {DynamicFormReadyEvent} from '../../events/dynamic-form-ready.event';
import {DynamicFormAutofillService} from '../../autofill/dynamic-form-autofill.service';
import {FlexLayoutConfig} from '../../layouts/flex-layout/flex-layout.config';
import {CustomFieldTemplateDirective} from '../../fields/custom/directives/custom-field-template.directive';
import {DynamicFieldComponent} from '../../dynamic-field/dynamic-field-component';
import {CustomFieldService} from '../../fields/custom/custom-field.service';


@UntilDestroy()
@Component({
    exportAs: 'dynamicForm',
    selector: 'mydocma-dynamic-form',
    styleUrls: ['dynamic-form.component.scss'],
    templateUrl: './dynamic-form.component.html',
    providers: [
        DynamicFormEventsService,
        DynamicFormService,
        DynamicFormValidationService,
        EventDispatcherService,
        DynamicFormAutofillService
    ]
})
export class DynamicFormComponent implements OnChanges, AfterContentInit, OnDestroy {

    @ContentChildren(CustomFieldTemplateDirective)
    public typeTemplates: QueryList<CustomFieldTemplateDirective>;

    /**
     * Stores name of current layout from formConfig layouts list.
     */
    @Input()
    public layoutConfig;

    @Input()
    public formConfig: DynamicFormConfig;

    @Input()
    public fieldTypeToControlBuilder: FieldTypeToControlBuilder = fieldTypeToControlBuilder;

    @Input()
    public fieldTypeToControlUpdater: FieldTypeToControlUpdater = fieldTypeToControlUpdater;

    @Input()
    public fieldTypeToComponent = FieldTypeToComponent;

    @Output()
    public submitted = new EventEmitter<any>();

    @Output()
    public ready = new EventEmitter<DynamicFormReadyEvent>();

    public formGroup: UntypedFormGroup;

    public flexLayoutConfig: FlexLayoutConfig;

    private initialized = false;

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly dynamicFormCustomFieldService: CustomFieldService,
        public readonly dynamicFormEventsService: DynamicFormEventsService,
        public readonly dynamicFormValidationService: DynamicFormValidationService,
        public readonly eventDispatcher: EventDispatcherService,
    ) {
        this.dynamicFormService.formGroup$
            .pipe(untilDestroyed(this))
            .subscribe(formGroup => {
                this.formGroup = formGroup;

                const self = this;
                const origFuncTouched = this.formGroup.markAsTouched;
                this.formGroup.markAsTouched = function () {
                    origFuncTouched.apply(this, arguments);

                    self.eventDispatcher.notifyFormTouched();
                };

                this.ready.emit({
                    dynamicFormService: this.dynamicFormService,
                    dynamicFormEventsService: this.dynamicFormEventsService,
                    dynamicFormValidationService: this.dynamicFormValidationService
                });
            });

        this.dynamicFormEventsService.onSubmit
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.submitted.emit();
            });
    }

    public get value(): any {
        return this.formGroup.value;
    }

    /**
     * A lifecycle hook that is called when any data-bound property of a directive changes.
     *
     * @param changes - changes
     */
    public ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }

        if (changes.formConfig) {
            this.onFormConfigChanged();
        }

        if (changes.layoutConfig ) {
            this.formConfig.layout = this.layoutConfig;

            const layout = this.formConfig.layouts.find(value => value.name === this.layoutConfig);
            if (!layout) {
                this.flexLayoutConfig = null;

                return;
            }

            this.flexLayoutConfig = layout;
            if (this.formGroup && this.formGroup.touched && this.formGroup.invalid) {
                setTimeout(() => this.dynamicFormValidationService.validate());
            }

        }
    }

    public ngAfterContentInit(): void {
        this.initialized = true;

        const typeTemplatesMap: Map<string, TemplateRef<DynamicFieldComponent>> = new Map();
        this.typeTemplates.forEach(typeTemplate => {
            typeTemplatesMap.set(typeTemplate.mydocmaCustomField, typeTemplate.template);
        });

        this.dynamicFormCustomFieldService.setTemplates(typeTemplatesMap);

        this.onFormConfigChanged();
    }

    public handleSubmit(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        this.submitted.emit(this.value);
    }

    /**
     * A lifecycle hook that is called when a directive, pipe, or service is destroyed.
     */
    public ngOnDestroy(): void {
    }

    private onFormConfigChanged() {
        if (!this.formConfig) {
            console.error(`Config is not valid.`);

            return;
        }

        this.flexLayoutConfig = null;

        const layout = this.formConfig.layouts.find(value => value.name === this.layoutConfig);
        if (layout) {
            this.flexLayoutConfig = layout;
        }
        if (!this.flexLayoutConfig) {
            this.flexLayoutConfig = this.formConfig.layouts[0];
        }

        this.dynamicFormService.createForm(
            this.formConfig,
            this.fieldTypeToControlBuilder,
            this.fieldTypeToControlUpdater
        );
    }
}
